import React, { useCallback, useRef, useState } from 'react';
import { message, Table, TablePaginationConfig } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ShopStatus, ShopType } from '../../../types/shop';
import { PRODUCT_TILE } from '../../../constants/define/translation';
import { ActionsColumn, IndexColumn, ShowHideColumn, StatusColumn, TitleColumn, CountryColumn } from './columns';
import { appStyles } from '../../../styles';
import { postSortTileWidgetsRepository } from '../../../repository/mytm/shop/shopRepository';
import { MESSAGE_ERROR } from '../../../constants/message';
import {
  WIDGET_TYPE,
  getScheduleTileWidgetById,
} from '../../../repository/mytm/schedule-tiles/scheduleTilesRepository';

interface ProductTileTableProps {
  records: ShopType[];
  onRefresh: () => void;
  setIsOpenModal: Function;
  setDataRecord: Function;
  setIsOpenScheduleModal: Function;
  setDataScheduleRecord: Function;
  handleChangePageActive: Function;
  loading?: boolean;
  setLoading: (value: boolean) => void;
  className?: string;
  isDraft?: boolean;
  pagination?: TablePaginationConfig;
}

const DraggableBodyRow = ({ index, moveRow, style, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr ref={ref} className={`${isOver ? dropClassName : ''}`} style={{ cursor: 'move', ...style }} {...restProps} />
  );
};

const { Column } = Table;

export const ProductTileTable = (props: ProductTileTableProps) => {
  const { t } = useTranslation(PRODUCT_TILE);
  const [isLoading, setIsLoading] = useState(false);
  const { pagination, records, loading, className, onRefresh, setDataScheduleRecord } = props;
  const countItemActive = records.filter(val => val.status === ShopStatus.ACTIVE).length;
  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setIsLoading(true);
      const dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);
      const recordSorts = dragRecordsList.map((record, index) => {
        return {
          id: record.id,
          sortOrder: index + 1,
        };
      });

      postSortTileWidgetsRepository({
        sortOrderList: recordSorts,
      })
        .then(() => {
          setIsLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setIsLoading(false);
        });
    },
    [records, onRefresh],
  );
  const handleGetScheduleWidgetByID = async (widgetId: string) => {
    setIsLoading(true);
    try {
      const widget = await getScheduleTileWidgetById(widgetId, WIDGET_TYPE.MARQUEE);

      if (!widget) {
        setDataScheduleRecord({ widgetId, widgetType: WIDGET_TYPE.MARQUEE });
      } else {
        setDataScheduleRecord(widget);
      }

      setIsLoading(false);
      return widget;
    } catch (e) {
      setIsLoading(false);
      return null;
    }
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledTable
          dataSource={records}
          loading={loading || isLoading}
          className={className}
          rowKey="index"
          pagination={pagination}
          components={components}
          summary={() => {
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={2} index={0}>
                    Total: {pagination?.total}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
          onRow={(recordDeep: any, index?: number): any => {
            return {
              index,
              moveRow,
            };
          }}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            width={32}
            dataIndex="index"
            key="index"
            render={value => <IndexColumn value={value} />}
          />
          <Column
            title={t('table.title.show')}
            align="center"
            width={100}
            dataIndex="show"
            key="show"
            render={(_, record: ShopType) => (
              <ShowHideColumn countItemActive={countItemActive} onRefresh={onRefresh} record={record} />
            )}
          />
          <Column
            title={t('table.title.country')}
            align="center"
            width={80}
            dataIndex="title"
            key="title"
            render={(_, record: ShopType) => <CountryColumn record={record} />}
          />
          <Column
            title={t('table.title.title')}
            align="center"
            width={80}
            dataIndex="title"
            key="title"
            render={(_, record: ShopType) => <TitleColumn record={record} />}
          />
          <Column
            title={t('table.title.status')}
            align="center"
            width={62}
            dataIndex="status"
            key="status"
            render={(_, record: ShopType) => (
              <StatusColumn
                status={record.status && record.status === ShopStatus.ACTIVE ? ShopStatus.ACTIVE : ShopStatus.INACTIVE}
                isActive={!!(record.status && record.status === ShopStatus.ACTIVE)}
                width="120px"
              />
            )}
          />
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            width={100}
            render={(_, record: ShopType) => (
              <ActionsColumn
                total={records.length}
                record={record}
                {...props}
                handleGetScheduleWidgetByID={handleGetScheduleWidgetByID}
              />
            )}
          />
        </StyledTable>
      </DndProvider>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }
`;
